import { action, computed, flow, makeObservable, observable } from 'mobx';
import { defaultFAQFilters } from '../constants/manageFAQ';
import { IFAQFilters, IFAQInList } from '../types/manageFAQ';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { IPagination, LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { booleanNumberReverse } from 'src/utils/helpers';
import { defaultPagination } from '~/constants/main';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

class ManageFAQStore {
  constructor() {
    makeObservable(this);
  }

  private getFAQListAbortController = getAbortController();
  private cancelStack: Record<string, AbortController> = {};

  @observable public loading: LoadingStatus = LoadingStatus.Init;

  @observable public pagination: IPagination<IFAQInList> = defaultPagination;
  @observable public filters: IFAQFilters = defaultFAQFilters;

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Refreshing, LoadingStatus.Loading].includes(this.loading);
  }

  @action public setFilters = (filters: Partial<IFAQFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.getFAQList(1);
  };

  @action public getFAQList = flow(function* (this: ManageFAQStore, page: number): any {
    this.getFAQListAbortController.abort();
    this.getFAQListAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params: any = {
        page,
        name: this.filters.search || '',
        status: this.filters.status,
        sort_field: this.filters.sortField || undefined,
        sort_order: this.filters.sortDir || undefined,
      };

      const response: IPagination<IFAQInList> = yield client.get('v4/website-info/faq', {
        params,
        signal: this.getFAQListAbortController.signal,
      });

      this.pagination = response;

      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'ManageFAQStore _getFAQList', showModal: true });
    }
  }).bind(this);

  @action public updateStatus = async (faqId: number, status: TBooleanInNumber) => {
    const cancelId = `faq-status_${faqId}`;

    try {
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId].abort();
      this.cancelStack[cancelId] = getAbortController();

      this.pagination.data = this.pagination.data.map((faq) => (faq.id === faqId ? { ...faq, status } : faq));

      await client.post(`v4/website-info/faq/set-status`, { id: faqId, status }, { signal: this.cancelStack[cancelId].signal });
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.pagination.data = this.pagination.data.map((faq) => (faq.id === faqId ? { ...faq, status: booleanNumberReverse(faq.status) } : faq));
      logger.error({ error, place: 'ManageFAQStore updateStatus', showModal: true });
    }
  };

  @action public deleteFAQ = async (faqId: number) => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.delete(`v4/website-info/faq/${faqId}`);

      this.getFAQList(this.pagination.current_page);
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'ManageFAQStore deleteFAQ', showModal: true });
    }
  };

  @action public unMount = () => {
    if (!window.location.pathname.includes(SCREENS.SETTINGS_WEBSITE_MANAGE_FAQ())) this.filters = defaultFAQFilters;
  };
}

const manageFAQStore = new ManageFAQStore();

export default manageFAQStore;
